import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, forkJoin } from 'rxjs';
import { MachineTypeEnum } from '../enums/machine-type-enum';
import { MachineService } from './machine.service';
import { EventTypeEnum } from '../enums/event-type-enum';
import { LocationTypeEnum } from '../enums/location-type-enum';
import { MarkerTypeEnum } from '../enums/marker-type-enum';
import { HelperFunctions } from '../helper/helper-functions';
import { DomSanitizer } from '@angular/platform-browser';
import { NGXLogger } from 'ngx-logger';
import { EventV2Dto, FieldV2Dto, LocationV2Dto, MachineV2Dto } from '../api/exa';
import { MapButtonTypeEnum } from '../enums/map-button-type-enum';

@Injectable({
  providedIn: 'root',
})
export class IconService {
  private offlineMarkerColor = 'bbbec2';

  private markerColorDefault = '27a89a'; // exagreen 2
  private textColorDefault = 'ffffff'; // white
  private eventColorDefault = '2196F3'; //
  private backgroundColorDefault = 'ffffff'; // white
  private backgroundOpacityDefault = 0.9; // 90%
  private widthDefault = 80; // 80px

  // machine marker
  private mapIconTemplateUrlTRACTOR = '/assets/icon_templates/machines/tractor-marker-template.svg';
  private mapIconTemplateTRACTOR = '';
  private mapIconTemplateUrlCOMBINE = '/assets/icon_templates/machines/combine-marker-template.svg';
  private mapIconTemplateCOMBINE = '';
  private mapIconTemplateUrlFORAGE_HARVESTER = '/assets/icon_templates/machines/forage-harvester-marker-template.svg';
  private mapIconTemplateFORAGE_HARVESTER = '';
  private mapIconTemplateUrlUNIMOG = '/assets/icon_templates/machines/unimog-marker-template.svg';
  private mapIconTemplateUNIMOG = '';
  private mapIconTemplateUrlTRUCK = '/assets/icon_templates/machines/truck-marker-template.svg';
  private mapIconTemplateTRUCK = '';
  private mapIconTemplateUrlEXCAVATOR = '/assets/icon_templates/machines/excavator-marker-template.svg';
  private mapIconTemplateEXCAVATOR = '';
  private mapIconTemplateUrlWHEELLOADER = '/assets/icon_templates/machines/wheelloader-marker-template.svg';
  private mapIconTemplateWHEELLOADER = '';
  private mapIconTemplateUrlTELEHANDLER = '/assets/icon_templates/machines/telehandler-marker-template.svg';
  private mapIconTemplateTELEHANDLER = '';
  private mapIconTemplateUrlSELF_PROPELLED = '/assets/icon_templates/machines/self-propelled-marker-template.svg';
  private mapIconTemplateSELF_PROPELLED = '';
  private mapIconTemplateUrlCYBERTRUCK = '/assets/icon_templates/machines/cybertruck-marker-template.svg';
  private mapIconTemplateCYBERTRUCK = '';

  // location marker
  private mapIconTemplateUrlFARM = '/assets/icon_templates/farm-marker-template.svg';
  private mapIconTemplateFARM = '';
  private mapIconTemplateUrlFUEL_STATION = '/assets/icon_templates/fuel-station-marker-template.svg';
  private mapIconTemplateFUEL_STATION = '';
  private mapIconTemplateUrlWEIGHING_STATION = '/assets/icon_templates/weighing-marker-template.svg';
  private mapIconTemplateWEIGHING_STATION = '';

  // field marker
  private mapIconTemplateUrlENTRANCE = '/assets/icon_templates/entrance-marker-template.svg';
  private mapIconTemplateENTRANCE = '';
  private mapIconTemplateUrlEXIT = '/assets/icon_templates/exit-marker-template.svg';
  private mapIconTemplateEXIT = '';
  private mapIconTemplateUrlINFO = '/assets/icon_templates/info-marker-template.svg';
  private mapIconTemplateINFO = '';
  private mapIconTemplateUrlWARNING = '/assets/icon_templates/warning-marker-template.svg';
  private mapIconTemplateWARNING = '';

  private mapIconTemplateUrlFIELD_NAME = '/assets/icon_templates/field-name-marker-template.svg';
  private mapIconTemplateFIELD_NAME = '';

  private mapIconTemplateUrlDEFAULT = '/assets/icon_templates/default-marker-template.svg';
  private mapIconTemplateDEFAULT = '';

  // Map Common Buttons
  private mapIconTemplateUrlBtnDeletePoint = '/assets/icon_templates/buttons/remove_circle-24px.svg';
  private mapIconTemplateBtnDeletePoint = '';

  constructor(
    private httpClient: HttpClient,
    private machineService: MachineService,
    private log: NGXLogger
  ) {
    this.loadMachineMapIcons();
    this.loadMiscMapIcons();
    this.loadMapButtonIcons();

    this.log.debug('Construct');
  }

  private loadMachineMapIcons() {
    forkJoin(
      this.readMapIconTemplate(this.mapIconTemplateUrlTRACTOR),
      this.readMapIconTemplate(this.mapIconTemplateUrlCOMBINE),
      this.readMapIconTemplate(this.mapIconTemplateUrlFORAGE_HARVESTER),
      this.readMapIconTemplate(this.mapIconTemplateUrlUNIMOG),
      this.readMapIconTemplate(this.mapIconTemplateUrlTRUCK),
      this.readMapIconTemplate(this.mapIconTemplateUrlEXCAVATOR),
      this.readMapIconTemplate(this.mapIconTemplateUrlCYBERTRUCK),
      this.readMapIconTemplate(this.mapIconTemplateUrlWHEELLOADER),
      this.readMapIconTemplate(this.mapIconTemplateUrlSELF_PROPELLED),
      this.readMapIconTemplate(this.mapIconTemplateUrlTELEHANDLER)
    ).subscribe(
      (data) => {
        // Update the icon Template
        this.mapIconTemplateTRACTOR = data[0];
        this.mapIconTemplateCOMBINE = data[1];
        this.mapIconTemplateFORAGE_HARVESTER = data[2];
        this.mapIconTemplateUNIMOG = data[3];
        this.mapIconTemplateTRUCK = data[4];
        this.mapIconTemplateEXCAVATOR = data[5];
        this.mapIconTemplateCYBERTRUCK = data[6];
        this.mapIconTemplateWHEELLOADER = data[7];
        this.mapIconTemplateSELF_PROPELLED = data[8];
        this.mapIconTemplateTELEHANDLER = data[9];

        this.log.debug('machine map icons loaded');
      },
      (err) => {
        this.log.debug(err);
      }
    );
  }

  private loadMiscMapIcons() {
    forkJoin(
      this.readMapIconTemplate(this.mapIconTemplateUrlFARM),
      this.readMapIconTemplate(this.mapIconTemplateUrlFUEL_STATION),
      this.readMapIconTemplate(this.mapIconTemplateUrlWEIGHING_STATION),
      this.readMapIconTemplate(this.mapIconTemplateUrlINFO),
      this.readMapIconTemplate(this.mapIconTemplateUrlWARNING),
      this.readMapIconTemplate(this.mapIconTemplateUrlENTRANCE),
      this.readMapIconTemplate(this.mapIconTemplateUrlEXIT),
      this.readMapIconTemplate(this.mapIconTemplateUrlDEFAULT),
      this.readMapIconTemplate(this.mapIconTemplateUrlFIELD_NAME)
    ).subscribe(
      (data) => {
        // Update the icon Template
        this.mapIconTemplateFARM = data[0];
        this.mapIconTemplateFUEL_STATION = data[1];
        this.mapIconTemplateWEIGHING_STATION = data[2];
        this.mapIconTemplateINFO = data[3];
        this.mapIconTemplateWARNING = data[4];
        this.mapIconTemplateENTRANCE = data[5];
        this.mapIconTemplateEXIT = data[6];
        this.mapIconTemplateDEFAULT = data[7];
        this.mapIconTemplateFIELD_NAME = data[8];

        this.log.debug('misc map icons loaded');
      },
      (err) => {
        this.log.debug(err);
      }
    );
  }

  private loadMapButtonIcons() {
    forkJoin(this.readMapIconTemplate(this.mapIconTemplateUrlBtnDeletePoint)).subscribe(
      (data) => {
        // Update the icon Template
        this.mapIconTemplateBtnDeletePoint = data[0];
        this.log.debug('map button icons loaded');
      },
      (err) => {
        this.log.debug(err);
      }
    );
  }

  private readMapIconTemplate(template_url: string): Observable<string> {
    const headers = new HttpHeaders().set('Content-Type', 'text/plain; charset=utf-8');
    return this.httpClient.get(template_url, { headers, responseType: 'text' });
  }

  getAppIconNameMachine(machine: MachineV2Dto): string {
    return this.getAppIconNameMachineType(machine.type, machine.make);
  }

  getAppIconNameMachineType(machineType: string, machineMake: string): string {
    let iconName;
    switch (machineType) {
      case MachineTypeEnum.TRACTOR:
        if (machineMake.toUpperCase() === 'DaimlerChrysler'.toUpperCase() || machineMake.toUpperCase() === 'Mercedes-Benz'.toUpperCase()) {
          iconName = 'unimog';
        } else {
          iconName = 'tractor';
        }
        break;
      case MachineTypeEnum.COMBINE:
        iconName = 'combine';
        break;
      case MachineTypeEnum.FORAGE_HARVESTER:
        iconName = 'harvester';
        break;
      case MachineTypeEnum.TRUCK:
        iconName = 'truck';
        break;
      case MachineTypeEnum.SYREN:
        iconName = 'tractor';
        break;
      case MachineTypeEnum.EXCAVATOR:
        iconName = 'excavator';
        break;
      case MachineTypeEnum.WHEELLOADER:
        iconName = 'wheelloader';
        break;
      case MachineTypeEnum.TELEHANDLER:
        iconName = 'telehandler';
        break;
      case MachineTypeEnum.SELF_PROPELLED:
        iconName = 'self-propelled';
        break;
      default:
        iconName = 'tractor';
        break;
    }

    return iconName;
  }

  getOwnPositionMarker() {
    let markerColor = '27a89a'; // fallBack if color not found
    let headerText = '';
    //headerText = this.machineService.getFormattedName(machine);
    return this.getMapMarker(this.mapIconTemplateDEFAULT, markerColor, null, null, null, headerText);
  }

  getMachineMapIcon(machine: MachineV2Dto, isOffline?: boolean, choosenMapTypeId?: number) {
    let markerColor = 'fcfcfc'; // fallBack if color not found
    let headerText = '';
    let colorText =  isOffline && choosenMapTypeId === 0 ? '0f9944' : isOffline && choosenMapTypeId === 1 ? 'a0ff9e' : 'ffffff'; // lime green

    // let colorText =  isOffline ? '4dc47b' : 'ffffff'; // lime green


    if (machine.color) {
      markerColor = machine.color;
    }
    // if (isOffline) {
    //   markerColor = this.offlineMarkerColor;
    // }
    headerText = this.machineService.getFormattedName(machine);
    

    let iconTemplate;
    switch (machine.type) {
      case MachineTypeEnum.TRACTOR:
        if (
          machine.make.toUpperCase() === 'DaimlerChrysler'.toUpperCase() ||
          machine.make.toUpperCase() === 'Mercedes-Benz'.toUpperCase()
        ) {
          iconTemplate = this.mapIconTemplateUNIMOG;
        } else {
          iconTemplate = this.mapIconTemplateTRACTOR;
        }
        break;
      case MachineTypeEnum.COMBINE:
        iconTemplate = this.mapIconTemplateCOMBINE;
        break;
      case MachineTypeEnum.FORAGE_HARVESTER:
        iconTemplate = this.mapIconTemplateFORAGE_HARVESTER;
        break;
      case MachineTypeEnum.TRUCK:
        iconTemplate = this.mapIconTemplateTRUCK;
        break;
      case MachineTypeEnum.SYREN:
        iconTemplate = this.mapIconTemplateTRACTOR;
        break;
      case MachineTypeEnum.EXCAVATOR:
        iconTemplate = this.mapIconTemplateEXCAVATOR;
        break;
      case MachineTypeEnum.WHEELLOADER:
        iconTemplate = this.mapIconTemplateWHEELLOADER;
        break;
      case MachineTypeEnum.TELEHANDLER:
        iconTemplate = this.mapIconTemplateTELEHANDLER;
        break;
      case MachineTypeEnum.SELF_PROPELLED:
        iconTemplate = this.mapIconTemplateSELF_PROPELLED;
        break;
      default:
        iconTemplate = this.mapIconTemplateCYBERTRUCK;
        break;
    }

    return this.getMapMarker(iconTemplate, markerColor, colorText, null, null, headerText);
  }

  getMarkerMapIcon(type: MarkerTypeEnum, markerColor?: string) {
    let iconTemplate;
    switch (type) {
      case MarkerTypeEnum.ENTRANCE:
        iconTemplate = this.mapIconTemplateENTRANCE;
        break;
      case MarkerTypeEnum.EXIT:
        iconTemplate = this.mapIconTemplateEXIT;
        break;
      case MarkerTypeEnum.INFO:
        iconTemplate = this.mapIconTemplateINFO;
        break;
      case MarkerTypeEnum.WARNING:
        iconTemplate = this.mapIconTemplateWARNING;
        break;
      default:
        iconTemplate = this.mapIconTemplateDEFAULT;
        break;
    }

    return this.getMapMarker(iconTemplate, markerColor, null, null, null, null);
  }

  getFieldMapIcon(field: FieldV2Dto) {
    let markerColor = this.markerColorDefault; // fallBack if color not found
    let headerText = field.name;
    // if (field.identifier && field.identifier.length > 0) {
    //   headerText += ' (' + field.identifier + ')';
    // }

    // TODO set color of location

    let iconTemplate = this.mapIconTemplateFIELD_NAME;

    return this.getMapMarker(iconTemplate, markerColor, null, null, null, headerText, true);
  }

  getLocationMapIcon(location: LocationV2Dto) {
    let markerColor = this.markerColorDefault; // fallBack if color not found
    let headerText = location.name;
    // if (location.comment && location.comment.length > 0) {
    //   headerText += ': ' + location.comment;
    // }

    // TODO set color of location

    let iconTemplate;
    switch (location.type) {
      case LocationTypeEnum.FARM:
        iconTemplate = this.mapIconTemplateFARM;
        break;
      case LocationTypeEnum.FUELSTATION:
        iconTemplate = this.mapIconTemplateFUEL_STATION;
        break;
      case LocationTypeEnum.WEIGHINGSTATION:
        iconTemplate = this.mapIconTemplateWEIGHING_STATION;
        break;
      default:
        iconTemplate = this.mapIconTemplateDEFAULT;
        break;
    }

    return this.getMapMarker(iconTemplate, markerColor, null, null, null, headerText);
  }

  getMapButtonIcon(mapButtonType: MapButtonTypeEnum, buttonColor?: string) {
    let markerColor = this.markerColorDefault; // fallBack if color not found
    if (buttonColor) {
      markerColor = buttonColor;
    }
    let iconTemplate = '';
    if (mapButtonType == MapButtonTypeEnum.DELETE_POINT) {
      iconTemplate = this.mapIconTemplateBtnDeletePoint;
    }
    // console.log('iconTemplate', iconTemplate)
    let ret = this.getMapMarker(iconTemplate, null, null, null, null, null, null, 24);
    // console.log('iconTemplate after edit', ret)
    return ret;
  }

  getEventMapIcon(event: EventV2Dto, title?: string) {
    let markerColor = this.eventColorDefault; // fallBack if color not found
    let headerText = title;

    // TODO set color of event

    let iconTemplate;
    switch (event.type) {
      case EventTypeEnum.COMMENT:
        iconTemplate = this.mapIconTemplateINFO;
        break;
      case EventTypeEnum.REFUELING:
        iconTemplate = this.mapIconTemplateFUEL_STATION; // TODO evtl besseres icon, weils das selbe wie die tankstelle ist ;)
        break;
      default:
        iconTemplate = this.mapIconTemplateINFO;
        break;
    }

    return this.getMapMarker(iconTemplate, markerColor, null, null, null, headerText);
  }

  getFieldNameMarker(field: FieldV2Dto) {
    let headerText = field.name;
    let iconTemplate = this.mapIconTemplateFIELD_NAME;
    return this.getMapMarker(iconTemplate, null, null, null, null, headerText);
  }

  encodeStringForSVG(inputStr: string): string {
    let outputStr = '';
    outputStr = HelperFunctions.replaceAll(inputStr, '&', '&amp;');
    outputStr = HelperFunctions.replaceAll(outputStr, "'", '&apos;');
    outputStr = HelperFunctions.replaceAll(outputStr, '"', '&quot;');
    outputStr = HelperFunctions.replaceAll(outputStr, '<', '&lt;');
    outputStr = HelperFunctions.replaceAll(outputStr, '>', '&gt;');
    return outputStr;
  }

  getMapMarker(
    template: string,
    markerColor: string,
    textColor: string,
    backgroundColor: string,
    backgroundOpacity: number,
    headerText: string,
    onlyText?: boolean,
    width?: number
  ) {
    const baseHeight = 1000;
    const baseWidth = 1800;
    const maxCharsPerRow = 12;
    const heightTranslationPerRow = 300;
    let height = baseHeight;
    let rowCount = 0;
    let yOffset = 220;

    if (!markerColor) {
      markerColor = this.markerColorDefault;
    } else {
      markerColor = HelperFunctions.replaceAll(markerColor, '#', '');
    }
    if (!textColor) {
      textColor = this.textColorDefault;
    } else {
      textColor = HelperFunctions.replaceAll(textColor, '#', '');
    }
    if (!backgroundColor) {
      backgroundColor = this.backgroundColorDefault;
    } else {
      backgroundColor = HelperFunctions.replaceAll(backgroundColor, '#', '');
    }
    if (!backgroundOpacity) {
      backgroundOpacity = this.backgroundOpacityDefault;
    }
    if (!width) {
      width = this.widthDefault;
    }
    if (onlyText && onlyText == true) {
      onlyText = true;
    } else {
      onlyText = false;
    }

    //style="font-weight:bold;font-size:280px; color: white; text-shadow: -1px 0 #000, 0 1px #000, 1px 0 #000, 0 -1px #000;"
   let headerContent = '';
if (headerText && headerText.length > 0) {
  // Split text into words
  const words = headerText.split(' ');
  let currentLine = '';
  let firstRow = true;

  for (let word of words) {
    if (!word) continue;
    
    // If single word is longer than maxCharsPerRow, split it
    if (word.length > maxCharsPerRow) {
      // Write current line if exists
      if (currentLine) {
        if (firstRow) {
          headerContent += `<tspan x="${baseWidth/2}" text-anchor="middle">${this.encodeStringForSVG(currentLine.trim())}</tspan>`;
          firstRow = false;
        } else {
          headerContent += `<tspan x="${baseWidth/2}" text-anchor="middle" dy="${heightTranslationPerRow}">${this.encodeStringForSVG(currentLine.trim())}</tspan>`;
        }
        rowCount++;
        currentLine = '';
      }

      // Handle long word by splitting it
      while (word.length > 0) {
        let chunk = word.substring(0, maxCharsPerRow - 2);
        word = word.substring(maxCharsPerRow - 2);

        if (firstRow) {
          headerContent += `<tspan x="${baseWidth/2}" text-anchor="middle">${this.encodeStringForSVG(chunk)}</tspan>`;
          firstRow = false;
        } else {
          headerContent += `<tspan x="${baseWidth/2}" text-anchor="middle" dy="${heightTranslationPerRow}">${this.encodeStringForSVG(chunk)}</tspan>`;
        }
        rowCount++;
      }
    }
    // Handle normal word that fits on current line
    else if ((currentLine + ' ' + word).trim().length <= maxCharsPerRow) {
      currentLine += (currentLine ? ' ' : '') + word;
    }
    // Word makes line too long, start new line
    else {
      if (currentLine) {
        if (firstRow) {
          headerContent += `<tspan x="${baseWidth/2}" text-anchor="middle">${this.encodeStringForSVG(currentLine.trim())}</tspan>`;
          firstRow = false;
        } else {
          headerContent += `<tspan x="${baseWidth/2}" text-anchor="middle" dy="${heightTranslationPerRow}">${this.encodeStringForSVG(currentLine.trim())}</tspan>`;
        }
        rowCount++;
      }
      currentLine = word;
    }
  }

  // Write final line if any
  if (currentLine) {
    if (firstRow) {
      headerContent += `<tspan x="${baseWidth/2}" text-anchor="middle">${this.encodeStringForSVG(currentLine.trim())}</tspan>`;
    } else {
      headerContent += `<tspan x="${baseWidth/2}" text-anchor="middle" dy="${heightTranslationPerRow}">${this.encodeStringForSVG(currentLine.trim())}</tspan>`;
    }
    rowCount++;
  }

  headerContent += '</text>';

  if (onlyText) {
    yOffset = baseHeight;
  }
  const headerHead = '<text x="0" y="' + yOffset + '" fill="#' + textColor + 
    '" font-family="Roboto, Helvetica, sans-serif" font-weight="bold" font-size="280px">';
  headerContent = headerHead + headerContent;
    }

    

    let heightOffset;
    if (rowCount <= 1) {
      heightOffset = heightTranslationPerRow;
    } else {
      heightOffset = heightTranslationPerRow * rowCount;
    }
    height += heightOffset;

    const iconTemplate = template
      .replace(new RegExp('{{markerColor}}', 'g'), '#' + markerColor)
      .replace(new RegExp('{{backgroundColor}}', 'g'), '#' + backgroundColor)
      .replace(new RegExp('{{backgroundOpacity}}', 'g'), backgroundOpacity.toString())
      .replace(new RegExp('{{headerContent}}', 'g'), headerContent)
      .replace(new RegExp('{{height}}', 'g'), height + '')
      .replace(new RegExp('{{heightTranslation}}', 'g'), heightOffset + '');      

    return {
      url: 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(iconTemplate),
      scaledSize: {
        width: width,
        height: width * (height / baseWidth),
      },
    };
  }
}
